"use client";

import { useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useCommunicationsHooks } from "@/lib/hooks/api/communications";
import { useTaskHooks } from "@/lib/hooks/api/shared/graphql-hooks";
import type { Contact_Tasks } from "@/src/gql/graphql";
import type { CommunicationFormSubmission } from "./activity-forms/CommunicationForm";
import type { TaskFormSubmission } from "./activity-forms/TaskForm";

/**
 * Provides submit handlers for the Log Activity modal's communication and task forms.
 */
export function useLogActivitySubmit(effectiveContactId: string | undefined, userId: string | undefined) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const activityHooks = useCommunicationsHooks();
  const taskHooks = useTaskHooks();
  const logCommunicationMutation = activityHooks.useCreate();
  const createTaskMutation = taskHooks.useCreate();

  const isLoggingCommunication = logCommunicationMutation.isPending;
  const isCreatingTask = createTaskMutation.isPending;
  const isLoading = isLoggingCommunication || isCreatingTask || isSubmitting;

  const handleCommunicationSubmit = useCallback(
    async (data: CommunicationFormSubmission): Promise<void> => {
      if (!effectiveContactId || !userId) {
        toast.error("No contact or user selected");
        return;
      }

      setIsSubmitting(true);
      try {
        const { follow_up_schedule, follow_up_due_date, follow_up_note, ...communicationInput } = data;
        await logCommunicationMutation.mutateAsync(communicationInput);

        if (follow_up_schedule && follow_up_due_date && follow_up_note) {
          const taskPayload: Partial<Contact_Tasks> = {
            title: follow_up_note,
            due_date: follow_up_due_date.toISOString(),
            contact_id: effectiveContactId,
            user_id: userId,
            status: "pending",
            priority: "medium",
            description: `Follow-up from communication: ${communicationInput.title ?? "Communication"}`,
          };
          await createTaskMutation.mutateAsync(taskPayload);
          toast.success("Communication logged and follow-up scheduled");
          void queryClient.invalidateQueries({ queryKey: ["tasks"] });
        } else {
          toast.success("Communication logged successfully");
        }

        void queryClient.invalidateQueries({ queryKey: ["activities"] });
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : "Failed to log communication";
        toast.error(message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [createTaskMutation, effectiveContactId, logCommunicationMutation, queryClient, userId]
  );

  const handleTaskSubmit = useCallback(
    async (data: TaskFormSubmission): Promise<void> => {
      if (!effectiveContactId || !userId) {
        toast.error("No contact or user selected");
        return;
      }

      setIsSubmitting(true);
      try {
        const taskPayload: Partial<Contact_Tasks> = {
          title: data.title,
          description: data.description,
          status: data.status,
          priority: data.priority,
          tags: data.tags,
          assigned_users: data.assigned_to ? [data.assigned_to] : undefined,
          contact_id: effectiveContactId,
          user_id: userId,
          due_date: data.due_date?.toISOString(),
          completed_at: data.completed_at?.toISOString(),
        };
        await createTaskMutation.mutateAsync(taskPayload);
        toast.success("Task created successfully");
        void queryClient.invalidateQueries({ queryKey: ["tasks"] });
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : "Failed to create task";
        toast.error(message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [createTaskMutation, effectiveContactId, queryClient, userId]
  );

  return { handleCommunicationSubmit, handleTaskSubmit, isLoading };
}
